import { useEffect, useState, useCallback, useRef } from 'react';

import { toast } from 'sonner';



// Same-origin '' uses setupProxy.js → backend :8002 (avoids stale :8001 listener).
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const LS_KEY = 'snagcart_settings_cache_v1';

const SETTINGS_URL = `${API_URL}/api/gpt-pilot/settings`;



const FALLBACK = {

  core: {

    main_branch_updates: false,

    auto_select_template: true,

    context_optimization: true,

    event_logging: true,

    auto_switch_to_file: true,

    extended_thinking: false,
    autonomous_mode: true,
    full_stack_mode: true,
    skip_clarification: true,
  },

  beta: { experimental_models: false, parallel_agent_runs: false, voice_input: false },

  notifications: {

    toast_on_build_done: true,

    toast_on_agent_done: true,

    toast_on_chat_reply: true,

    sound_enabled: false,

  },

  project_memory: { enabled: true, memory_text: '' },

  providers: { default_text_model: 'gpt-4o-mini', default_vision_model: 'gpt-4o' },

  services: {

    github:   { connected: false },

    gitlab:   { connected: false },

    netlify:  { connected: false },

    vercel:   { connected: false },

    supabase: { connected: false },

    openhands: { connected: false, base_url: 'https://app.all-hands.dev' },

    railway:  { connected: false },

    tavily:   { connected: false },

    auto_deploy: { enabled: false },

  },

  cloud_providers: { all_enabled: true, keys: {} },

  prompt_library: [],

  mcp_servers: [],

};



function authHeaders() {

  const t = localStorage.getItem('token');

  return t ? { Authorization: `Bearer ${t}` } : {};

}



function deepMergeSettings(prev, patch) {

  const next = { ...prev };

  for (const k of Object.keys(patch)) {

    const pv = patch[k];

    const cv = prev[k];

    if (pv && typeof pv === 'object' && !Array.isArray(pv) && cv && typeof cv === 'object' && !Array.isArray(cv)) {

      next[k] = { ...cv };

      for (const sk of Object.keys(pv)) {

        const psv = pv[sk];

        const csv = cv[sk];

        if (psv && typeof psv === 'object' && !Array.isArray(psv) && csv && typeof csv === 'object' && !Array.isArray(csv)) {

          next[k][sk] = { ...csv, ...psv };

          if (sk === 'keys' && psv) {

            next[k][sk] = { ...csv };

            for (const pk of Object.keys(psv)) {

              next[k][sk][pk] = { ...(csv[pk] || {}), ...psv[pk] };

            }

          }

        } else {

          next[k][sk] = psv;

        }

      }

    } else {

      next[k] = pv;

    }

  }

  return next;

}



/**

 * useSnagcartSettings — App Builder settings persisted to Mongo via GPT Pilot API.

 */

export function useSnagcartSettings() {

  const [settings, setSettings] = useState(() => {

    try {

      const cached = localStorage.getItem(LS_KEY);

      return cached ? JSON.parse(cached) : FALLBACK;

    } catch {

      return FALLBACK;

    }

  });

  const [loading, setLoading] = useState(true);

  const [loadError, setLoadError] = useState(null);

  const [saving, setSaving] = useState(false);

  const settingsRef = useRef(settings);

  settingsRef.current = settings;



  const persistCache = useCallback((next) => {

    try { localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch { /* ignore */ }

  }, []);



  const reload = useCallback(async () => {

    const token = localStorage.getItem('token');

    if (!token) {

      setLoadError('not_authenticated');

      setLoading(false);

      return null;

    }

    setLoadError(null);

    try {

      const res = await fetch(SETTINGS_URL, { headers: authHeaders() });

      if (!res.ok) {

        throw new Error(`HTTP ${res.status}`);

      }

      const data = await res.json();

      setSettings(data);

      persistCache(data);

      return data;

    } catch (err) {

      const msg = err?.message || 'load failed';

      setLoadError(msg);

      console.warn('[useSnagcartSettings] load failed:', err);

      return null;

    } finally {

      setLoading(false);

    }

  }, [persistCache]);



  useEffect(() => {

    let cancelled = false;

    (async () => {

      await reload();

      if (cancelled) return;

    })();

    return () => { cancelled = true; };

  }, [reload]);



  const save = useCallback(async (patch) => {

    const token = localStorage.getItem('token');

    if (!token) {

      toast.error('Please log in to save settings');

      return { ok: false, error: 'not_authenticated' };

    }



    setSaving(true);

    const previous = settingsRef.current;

    const optimistic = deepMergeSettings(previous, patch);

    setSettings(optimistic);

    persistCache(optimistic);



    try {

      const res = await fetch(SETTINGS_URL, {

        method: 'PUT',

        headers: { 'Content-Type': 'application/json', ...authHeaders() },

        body: JSON.stringify(patch),

      });

      if (!res.ok) {

        const detail = await res.text().catch(() => '');

        throw new Error(detail || `Save failed (HTTP ${res.status})`);

      }

      const fresh = await res.json();

      setSettings(fresh);

      persistCache(fresh);

      return { ok: true, data: fresh };

    } catch (err) {

      setSettings(previous);

      persistCache(previous);

      const message = err?.message || 'Failed to save settings';

      toast.error(message.includes('503') ? 'Settings database unavailable — is MongoDB running?' : message);

      return { ok: false, error: message };

    } finally {

      setSaving(false);

    }

  }, [persistCache]);



  return { settings, loading, loadError, saving, save, reload };

}



export function readSnagcartSettings() {

  try {

    const cached = localStorage.getItem(LS_KEY);

    return cached ? JSON.parse(cached) : FALLBACK;

  } catch {

    return FALLBACK;

  }

}


